// Client-side notifications context
// Handles notifications based on order status changes without persisting to database
import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { queryKeys } from '@/lib/query-client';
import { useKitchenSpecialties } from '@/hooks/useKitchenSpecialties';
import type { Order, Staff, OrderItem } from '@/types/entities';

export interface ClientNotification {
  id: string;
  orderId: string;
  type:
    | 'order_submitted'
    | 'order_ready'
    | 'order_completed'
    | 'order_in_progress'
    | 'order_updated';
  messageAr: string;
  messageFr: string;
  isRead: boolean;
  createdAt: string;
  order?: Order;
}

interface ClientNotificationsContextType {
  notifications: ClientNotification[];
  unreadCount: number;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  clearNotifications: () => void;
}

const ClientNotificationsContext = createContext<ClientNotificationsContextType | undefined>(
  undefined
);

interface ClientNotificationsProviderProps {
  children: ReactNode;
  staff: Staff | null;
  restaurantId: string | null;
}

export function ClientNotificationsProvider({
  children,
  staff,
  restaurantId,
}: ClientNotificationsProviderProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [notifications, setNotifications] = useState<ClientNotification[]>([]);
  const { data: specialties = [] } = useKitchenSpecialties(restaurantId);

  // Ref to store the repeating sound interval for order ready notifications (server)
  const orderReadySoundIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Ref to store the repeating sound interval for new order notifications (kitchen)
  const newOrderSoundIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Function to stop the repeating sound (server - order ready)
  const stopOrderReadySound = useCallback(() => {
    if (orderReadySoundIntervalRef.current) {
      clearInterval(orderReadySoundIntervalRef.current);
      orderReadySoundIntervalRef.current = null;
    }
  }, []);

  // Function to stop the repeating sound (kitchen - new order)
  const stopNewOrderSound = useCallback(() => {
    if (newOrderSoundIntervalRef.current) {
      clearInterval(newOrderSoundIntervalRef.current);
      newOrderSoundIntervalRef.current = null;
    }
  }, []);

  // Helper function to find an order in cached data
  const findOrderInCache = useCallback(
    (orderId: string, cachedOrders: Order[] | undefined): Order | null => {
      if (!cachedOrders) return null;

      for (const parentOrder of cachedOrders) {
        if (parentOrder.subOrders) {
          const subOrder = parentOrder.subOrders.find((so) => so.id === orderId);
          if (subOrder) {
            return subOrder;
          }
        }
        // Also check if the order itself matches (in case it's a sub-order)
        if (parentOrder.id === orderId) {
          return parentOrder;
        }
      }
      return null;
    },
    []
  );

  // Helper function to fetch an order by ID (from cache with retries, minimal DB fallback)
  const fetchOrderById = useCallback(
    async (orderId: string, tableId: string): Promise<Order | null> => {
      // First, try cache immediately (no wait)
      let cachedOrders = queryClient.getQueryData<Order[]>(
        queryKeys.orders.list({ restaurantId: restaurantId || '' })
      );
      let order = findOrderInCache(orderId, cachedOrders);
      if (order) {
        return order;
      }

      // If not in cache, wait a bit for realtime subscription to update it
      // The realtime subscription in useOrders.ts invalidates and refetches, so cache should update quickly
      for (let attempt = 0; attempt < 3; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, 150)); // Wait 150ms between attempts

        cachedOrders = queryClient.getQueryData<Order[]>(
          queryKeys.orders.list({ restaurantId: restaurantId || '' })
        );
        order = findOrderInCache(orderId, cachedOrders);
        if (order) {
          return order;
        }
      }

      // Only as last resort: fetch from DB (but this should rarely happen)
      // Note: This fetches all order items for the restaurant, but it's a fallback
      // In practice, the cache should be populated by realtime updates before we get here
      try {
        const { data: orderItemsData } = await (supabase.rpc as any)('get_restaurant_order_items', {
          p_restaurant_id: restaurantId,
        });

        if (orderItemsData) {
          const orderItems = orderItemsData.filter((item: any) => item.order_id === orderId);
          if (orderItems.length > 0) {
            // Build a minimal order object for checking relevance
            return {
              id: orderId,
              tableId,
              serverId: '',
              status: 'pending' as any,
              createdAt: '',
              updatedAt: '',
              items: orderItems.map((item: any) => ({
                id: item.item_id,
                orderId: item.order_id,
                menuItemId: item.item_menu_item_id,
                menuItemOptionId: item.item_menu_item_option_id || null,
                quantity: item.item_quantity,
                priceAtOrder: item.item_price_at_order,
                notes: item.item_notes || null,
                createdAt: item.item_created_at || '',
                updatedAt: item.updated_at || '',
                specialtyIds: item.specialty_ids || [],
                portions:
                  item.portions && Array.isArray(item.portions)
                    ? item.portions.map((p: any) => ({
                        id: p.id,
                        orderItemId: item.item_id,
                        specialtyId: p.specialty_id,
                        status: p.status,
                        updatedAt: p.updated_at,
                        updatedBy: p.updated_by || null,
                        specialtyNameAr: p.specialty_name_ar,
                        specialtyNameFr: p.specialty_name_fr,
                        specialtyColor: p.specialty_color,
                      }))
                    : [],
              })) as OrderItem[],
            } as Order;
          }
        }
      } catch (error) {
        console.error('Failed to fetch order from DB:', error);
      }

      return null;
    },
    [queryClient, restaurantId, findOrderInCache]
  );

  // Helper function to check if an order is relevant to the staff's specialty
  // Uses the same logic as KitchenDashboard.tsx
  const isOrderRelevantToStaff = useCallback(
    (order: Order | null, staffSpecialtyId: string | null): boolean => {
      if (!order || !order.items || order.items.length === 0) {
        return false;
      }

      // Check each item in the order
      for (const item of order.items) {
        const itemPortions = item.portions || [];
        const itemSpecialties = item.specialtyIds || [];

        if (itemPortions.length > 0) {
          // Item has portions - filter by staff specialty
          const relevantPortions = staffSpecialtyId
            ? itemPortions.filter((p) => p.specialtyId === staffSpecialtyId)
            : itemPortions;

          if (relevantPortions.length > 0) {
            return true; // Found at least one relevant portion
          }
        } else if (itemSpecialties.length === 0) {
          // Item has no specialties - visible to all kitchen staff
          return true;
        } else {
          // Item has specialties but no portions (old orders)
          if (!staffSpecialtyId || itemSpecialties.includes(staffSpecialtyId)) {
            return true;
          }
        }
      }

      return false; // No relevant items found
    },
    []
  );

  // Sound notification function
  const playNotificationSound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const startTime = audioContext.currentTime;

      // Create a triple beep pattern for maximum attention (total duration: ~2.5 seconds)
      // Pattern: BEEP - pause - BEEP - pause - BEEP
      const beepPattern = [
        { start: 0, duration: 0.4 }, // First beep: 0-0.4s
        { start: 0.6, duration: 0.4 }, // Second beep: 0.6-1.0s
        { start: 1.3, duration: 0.4 }, // Third beep: 1.3-1.7s
      ];

      beepPattern.forEach(({ start, duration }) => {
        // Create oscillator for main tone
        const oscillator1 = audioContext.createOscillator();
        const gainNode1 = audioContext.createGain();

        // Create second oscillator for richer sound (harmonic)
        const oscillator2 = audioContext.createOscillator();
        const gainNode2 = audioContext.createGain();

        // Main tone: 1000 Hz (more noticeable than 800 Hz)
        oscillator1.frequency.value = 1000;
        oscillator1.type = 'sine';

        // Harmonic: 1500 Hz for richer, more powerful sound
        oscillator2.frequency.value = 1500;
        oscillator2.type = 'sine';

        // Connect both oscillators
        oscillator1.connect(gainNode1);
        oscillator2.connect(gainNode2);
        gainNode1.connect(audioContext.destination);
        gainNode2.connect(audioContext.destination);

        const noteStart = startTime + start;
        const noteEnd = noteStart + duration;

        // Powerful volume: 0.7 (was 0.3) - much louder
        // Quick attack and release for crisp sound
        gainNode1.gain.setValueAtTime(0, noteStart);
        gainNode1.gain.linearRampToValueAtTime(0.7, noteStart + 0.02); // Quick attack
        gainNode1.gain.setValueAtTime(0.7, noteEnd - 0.02);
        gainNode1.gain.linearRampToValueAtTime(0, noteEnd); // Quick release

        // Harmonic at slightly lower volume
        gainNode2.gain.setValueAtTime(0, noteStart);
        gainNode2.gain.linearRampToValueAtTime(0.4, noteStart + 0.02);
        gainNode2.gain.setValueAtTime(0.4, noteEnd - 0.02);
        gainNode2.gain.linearRampToValueAtTime(0, noteEnd);

        oscillator1.start(noteStart);
        oscillator1.stop(noteEnd);
        oscillator2.start(noteStart);
        oscillator2.stop(noteEnd);
      });
    } catch (error) {
      console.error('Failed to play notification sound:', error);
    }
  }, []);

  // Use refs to store latest handler functions for stable event listener cleanup
  const handlersRef = useRef<{
    handleOrderInserted: ((event: CustomEvent) => Promise<void>) | null;
    handleOrderStatusChanged: ((event: CustomEvent) => Promise<void>) | null;
    handleOrderUpdated: ((event: CustomEvent) => Promise<void>) | null;
  }>({
    handleOrderInserted: null,
    handleOrderStatusChanged: null,
    handleOrderUpdated: null,
  });

  // Create stable wrapper functions that call the latest handlers from refs
  // These wrappers maintain the same reference across renders for proper cleanup
  const wrappedInsertedRef = useRef<(e: Event) => void>((e: Event) => {
    const handler = handlersRef.current.handleOrderInserted;
    if (handler) {
      void handler(e as CustomEvent);
    }
  });
  const wrappedStatusChangedRef = useRef<(e: Event) => void>((e: Event) => {
    const handler = handlersRef.current.handleOrderStatusChanged;
    if (handler) {
      void handler(e as CustomEvent);
    }
  });
  const wrappedOrderUpdatedRef = useRef<(e: Event) => void>((e: Event) => {
    const handler = handlersRef.current.handleOrderUpdated;
    if (handler) {
      void handler(e as CustomEvent);
    }
  });

  // Listen to order changes via custom events dispatched by useOrders hook
  useEffect(() => {
    if (!restaurantId || !staff) return;

    // Handler for new orders (INSERT events)
    const handleOrderInserted = async (event: CustomEvent) => {
      const { orderId, tableId, isSubOrder } = event.detail;

      // Skip notifications for parent order insertions (they're auto-created by trigger)
      // We only want notifications for actual sub-order insertions where work happens
      if (!isSubOrder) {
        return;
      }

      // Notify kitchen when new order is submitted with sound
      if (staff.role === 'kitchen') {
        // Fetch the order to check if it's relevant to this staff's specialty
        const relevantOrder = await fetchOrderById(orderId, tableId);

        // Check if the order is relevant to this staff's specialty
        const isRelevant = isOrderRelevantToStaff(relevantOrder, staff.specialtyId || null);

        if (!isRelevant) {
          return; // Don't notify if the order is not relevant to this staff's specialty
        }

        // Fetch table number
        const { data: tablesData } = await supabase.rpc('get_restaurant_tables', {
          p_restaurant_id: restaurantId,
        });

        const table = tablesData?.find((t: any) => t.id === tableId);
        const tableNumber = table?.table_number || '?';

        const notification: ClientNotification = {
          id: `notif-${orderId}-${Date.now()}`,
          orderId,
          type: 'order_submitted',
          messageAr: `طلب جديد للطاولة رقم ${tableNumber}`,
          messageFr: `Nouvelle commande pour la table ${tableNumber}`,
          isRead: false,
          createdAt: new Date().toISOString(),
        };
        setNotifications((prev) => [notification, ...prev]);

        // Stop any previous repeating sound first
        stopNewOrderSound();

        // Play sound immediately and then repeat every 4 seconds until kitchen accepts
        playNotificationSound();
        newOrderSoundIntervalRef.current = setInterval(() => {
          playNotificationSound();
        }, 4000);

        const toastTitle = t('notification.toast.newOrder.title');
        const toastDesc = t('notification.toast.newOrder.description', { tableNumber });
        const acceptText = t('notification.toast.newOrder.accept');

        // Show toast with action button - stays until dismissed
        const { dismiss } = toast({
          title: toastTitle,
          description: toastDesc,
          duration: 1000 * 60 * 60, // 1 hour - effectively infinite until action
          className: 'bg-orange-50 border-orange-500 text-orange-900 border-2',
          action: (
            <ToastAction
              altText={acceptText}
              onClick={() => {
                stopNewOrderSound();
                dismiss();
              }}
              className="bg-orange-600 text-white hover:bg-orange-700 border-orange-600"
            >
              {acceptText}
            </ToastAction>
          ),
        });
      }
    };

    // Handler for order status changes (UPDATE events)
    const handleOrderStatusChanged = async (event: CustomEvent) => {
      const { orderId, tableId, oldStatus, newStatus, isSubOrder } = event.detail;

      // Skip notifications for parent order status changes (they're auto-synced by trigger)
      // We only want notifications for actual sub-order changes where work happens
      if (!isSubOrder) {
        return;
      }

      // Fetch table number
      const { data: tablesData } = await supabase.rpc('get_restaurant_tables', {
        p_restaurant_id: restaurantId,
      });

      const table = tablesData?.find((t: any) => t.id === tableId);
      const tableNumber = table?.table_number || '?';

      let notification: ClientNotification | null = null;

      // Generate notification based on status change and staff role
      if (newStatus === 'ready' && oldStatus !== 'ready' && staff.role === 'server') {
        // Order is ready - notify server with sound
        notification = {
          id: `notif-${orderId}-${Date.now()}`,
          orderId,
          type: 'order_ready',
          messageAr: `الطلب جاهز للطاولة رقم ${tableNumber}`,
          messageFr: `Commande prête pour la table ${tableNumber}`,
          isRead: false,
          createdAt: new Date().toISOString(),
        };

        // Only play global notification sound and show toast if no specialties are configured
        // If specialties exist, we rely on portion-specific notifications from ServerDashboard instead
        // This prevents duplicate notifications when the last portion becomes ready
        const hasActiveSpecialties =
          specialties && specialties.length > 0 && specialties.some((s) => s.isActive);

        if (hasActiveSpecialties) {
          // Skip showing toast - ServerDashboard handles portion-specific notifications
          // This prevents duplicate "Order Ready" + "Portion Ready" toasts when last portion is ready
          console.log(
            'Skipping order-ready notification in ClientNotificationsContext - specialties are configured, using portion-specific notifications from ServerDashboard'
          );
          return; // Exit early, don't show toast or add to notifications
        }

        // Stop any previous repeating sound first
        stopOrderReadySound();

        // Play sound immediately and then repeat every 4 seconds until server accepts
        playNotificationSound();
        orderReadySoundIntervalRef.current = setInterval(() => {
          playNotificationSound();
        }, 4000);

        const toastTitle = t('notification.toast.orderReady.title');
        const toastDesc = t('notification.toast.orderReady.description', { tableNumber });
        const takeItText = t('notification.toast.orderReady.takeIt');

        // Show toast with action button - stays until dismissed
        const { dismiss } = toast({
          title: toastTitle,
          description: toastDesc,
          duration: 1000 * 60 * 60, // 1 hour - effectively infinite until action
          className: 'bg-green-50 border-green-500 text-green-900 border-2',
          action: (
            <ToastAction
              altText={takeItText}
              onClick={() => {
                stopOrderReadySound();
                dismiss();
              }}
              className="bg-green-600 text-white hover:bg-green-700 border-green-600"
            >
              {takeItText}
            </ToastAction>
          ),
        });
      } else if (
        newStatus === 'completed' &&
        oldStatus !== 'completed' &&
        staff.role === 'kitchen'
      ) {
        // Order completed by server - notify kitchen
        notification = {
          id: `notif-${orderId}-${Date.now()}`,
          orderId,
          type: 'order_completed',
          messageAr: `تم إكمال الطلب للطاولة رقم ${tableNumber}`,
          messageFr: `Commande complétée pour la table ${tableNumber}`,
          isRead: false,
          createdAt: new Date().toISOString(),
        };

        toast({
          title: t('notification.toast.orderCompleted.title'),
          description: t('notification.toast.orderCompleted.description', { tableNumber }),
          duration: 3000,
          className: 'bg-blue-50 border-blue-500 text-blue-900 border-2',
        });
      } else if (
        newStatus === 'in_progress' &&
        oldStatus === 'pending' &&
        staff.role === 'server'
      ) {
        // Kitchen started working on order - notify server
        notification = {
          id: `notif-${orderId}-${Date.now()}`,
          orderId,
          type: 'order_in_progress',
          messageAr: `الطلب قيد التحضير للطاولة رقم ${tableNumber}`,
          messageFr: `Commande en préparation pour la table ${tableNumber}`,
          isRead: false,
          createdAt: new Date().toISOString(),
        };

        toast({
          title: t('notification.toast.orderInProgress.title'),
          description: t('notification.toast.orderInProgress.description', { tableNumber }),
          duration: 3000,
          className: 'bg-yellow-50 border-yellow-500 text-yellow-900 border-2',
        });
      }

      if (notification) {
        setNotifications((prev) => [notification!, ...prev]);
      }
    };

    // Handler for order updates (item added/deleted)
    const handleOrderUpdated = async (event: CustomEvent) => {
      const { orderId, tableId, isSubOrder } = event.detail;

      // Skip notifications for parent order updates
      if (!isSubOrder) {
        return;
      }

      // Notify kitchen when order is updated (items added/deleted)
      if (staff.role === 'kitchen') {
        // Fetch the order to check if it's relevant to this staff's specialty
        const relevantOrder = await fetchOrderById(orderId, tableId);

        // Check if the order is relevant to this staff's specialty
        const isRelevant = isOrderRelevantToStaff(relevantOrder, staff.specialtyId || null);

        if (!isRelevant) {
          return; // Don't notify if the order is not relevant to this staff's specialty
        }

        // Fetch table number
        const { data: tablesData } = await supabase.rpc('get_restaurant_tables', {
          p_restaurant_id: restaurantId,
        });

        const table = tablesData?.find((t: any) => t.id === tableId);
        const tableNumber = table?.table_number || '?';

        const notification: ClientNotification = {
          id: `notif-${orderId}-${Date.now()}`,
          orderId,
          type: 'order_updated',
          messageAr: `تم تحديث الطلب للطاولة رقم ${tableNumber}`,
          messageFr: `Commande modifiée pour la table ${tableNumber}`,
          isRead: false,
          createdAt: new Date().toISOString(),
        };
        setNotifications((prev) => [notification, ...prev]);

        // Stop any previous repeating sound first
        stopNewOrderSound();

        // Play sound immediately and then repeat every 4 seconds until kitchen accepts
        playNotificationSound();
        newOrderSoundIntervalRef.current = setInterval(() => {
          playNotificationSound();
        }, 4000);

        const toastTitle = t('notification.toast.orderUpdated.title');
        const toastDesc = t('notification.toast.orderUpdated.description', { tableNumber });
        const acceptText = t('notification.toast.newOrder.accept');

        // Show toast with action button - stays until dismissed
        const { dismiss } = toast({
          title: toastTitle,
          description: toastDesc,
          duration: 1000 * 60 * 60, // 1 hour - effectively infinite until action
          className: 'bg-amber-50 border-amber-500 text-amber-900 border-2',
          action: (
            <ToastAction
              altText={acceptText}
              onClick={() => {
                stopNewOrderSound();
                dismiss();
              }}
              className="bg-amber-600 text-white hover:bg-amber-700 border-amber-600"
            >
              {acceptText}
            </ToastAction>
          ),
        });
      }
    };

    // Store latest handlers in ref so wrapper functions can access them
    handlersRef.current = {
      handleOrderInserted,
      handleOrderStatusChanged,
      handleOrderUpdated,
    };

    // Use stable wrapper functions from refs for event listeners
    window.addEventListener('order-inserted', wrappedInsertedRef.current);
    window.addEventListener('order-status-changed', wrappedStatusChangedRef.current);
    window.addEventListener('order-updated', wrappedOrderUpdatedRef.current);

    return () => {
      window.removeEventListener('order-inserted', wrappedInsertedRef.current);
      window.removeEventListener('order-status-changed', wrappedStatusChangedRef.current);
      window.removeEventListener('order-updated', wrappedOrderUpdatedRef.current);
    };
  }, [
    restaurantId,
    staff,
    specialties,
    t,
    playNotificationSound,
    queryClient,
    isOrderRelevantToStaff,
    fetchOrderById,
    stopOrderReadySound,
    stopNewOrderSound,
  ]);

  // Cleanup repeating sounds only on component unmount
  useEffect(() => {
    return () => {
      if (orderReadySoundIntervalRef.current) {
        clearInterval(orderReadySoundIntervalRef.current);
      }
      if (newOrderSoundIntervalRef.current) {
        clearInterval(newOrderSoundIntervalRef.current);
      }
    };
  }, []);

  const markAsRead = useCallback((notificationId: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <ClientNotificationsContext.Provider
      value={{
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        clearNotifications,
      }}
    >
      {children}
    </ClientNotificationsContext.Provider>
  );
}

export function useClientNotifications() {
  const context = useContext(ClientNotificationsContext);
  if (context === undefined) {
    throw new Error('useClientNotifications must be used within ClientNotificationsProvider');
  }
  return context;
}
